/**
 * ToolActivityView
 *
 * Timeline list of tool calls with expandable input/output JSON.
 * One item can be expanded at a time.
 */

import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import type { IToolActivityViewProps } from '@/@types/voiceAgents';

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function ToolActivityView({ toolLogs }: IToolActivityViewProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  if (toolLogs.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        No tool activity recorded
      </div>
    );
  }

  return (
    <div className="divide-y">
      {toolLogs.map((log, index) => {
        const isExpanded = expandedIndex === index;
        return (
          <div key={log._id} className="px-4 py-3">
            <Button
              variant="ghost"
              className="w-full justify-start gap-2 h-auto p-0 hover:bg-transparent"
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronRight className="w-4 h-4 shrink-0" />
              )}
              <Wrench className="w-4 h-4 shrink-0 text-muted-foreground" />
              <span className="font-mono text-sm font-medium">{log.toolName}</span>
              <Badge variant={log.allowed ? 'default' : 'destructive'} className="ml-2">
                {log.allowed ? 'Allowed' : 'Denied'}
              </Badge>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatTimestamp(log.timestamp)}
              </span>
            </Button>

            {isExpanded && (
              <div className="mt-2 ml-8 space-y-2">
                {!log.allowed && log.deniedReason && (
                  <p className="text-xs text-destructive">Reason: {log.deniedReason}</p>
                )}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Input</p>
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                    {log.input}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Output</p>
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                    {log.output}
                  </pre>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
